import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { X, ExternalLink } from "lucide-react";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useAuth } from '../App';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

// Configuración visual por defecto
const DEFAULT_CONFIG = {
  color_fondo_modal: "#ffffff",
  color_overlay: "#000000",
  opacidad_overlay: 50,
  imagen_ajuste: "contain",
  imagen_altura: "300",
  imagen_redondeo: "0",
  mostrar_degradado_imagen: false,
  color_degradado_inicio: "transparent",
  color_degradado_fin: "rgba(0,0,0,0.3)",
  color_titulo: "#1f2937",
  tamaño_titulo: "2xl",
  fuente_titulo: "sans",
  peso_titulo: "bold",
  color_descripcion: "#6b7280",
  tamaño_descripcion: "base",
  color_boton_fondo: "#3b82f6",
  color_boton_texto: "#ffffff",
  color_boton_hover: "#2563eb",
  boton_redondeo: "md",
  boton_tamaño: "md",
  color_boton_cerrar: "transparent",
  color_boton_cerrar_texto: "#6b7280",
  estilo_boton_cerrar: "ghost",
  modal_ancho: "lg",
  modal_redondeo: "xl",
  modal_sombra: "2xl",
  padding_contenido: "24",
  espacio_imagen_contenido: "0",
  espacio_titulo_descripcion: "8",
  espacio_descripcion_botones: "16",
  espacio_entre_botones: "12",
  animacion_entrada: "fade",
  duracion_animacion: 200
};

// Mapeo de rutas a IDs de menú/submenú
const RUTA_A_MENU = {
  '/': 'riesgos_generales',
  '/dashboard/mi-dashboard': 'mi_dashboard',
  '/dashboard/ejecutivo': 'dashboard_ejecutivo',
  '/compare': 'riesgos_generales',
  '/coberturas': 'riesgos_generales',
  '/reports': 'riesgos_generales',
  '/templates': 'riesgos_generales',
  '/riesgos-generales/plantillas-aseguradoras': 'riesgos_generales',
  '/riesgos-generales/control-llamadas': 'riesgos_generales',
  '/riesgos-generales/control-renovaciones': 'riesgos_generales',
  '/gestion-humana': 'gestion_humana',
  '/beneficios': 'riesgos_personas',
  '/mensajeria': 'mensajeria',
  '/mesa-ayuda': 'mesa_ayuda',
  '/whatsapp': 'whatsapp',
  '/inventario': 'finanzas',
  '/mobiliario': 'finanzas',
  '/caja-chica': 'finanzas',
  '/cuentas-pagar': 'finanzas',
  '/medicamentos': 'finanzas',
  '/visitantes': 'control_visitantes',
  '/requisiciones': 'requisiciones',
  '/encuestas': 'modulo_encuesta',
  '/estadisticas': 'estadisticas',
  '/converter': 'convertidor',
  '/publicidades': 'publicidades',
};

// Helper para obtener clases de tamaño de fuente
const getFontSizeClass = (size) => {
  const sizes = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl'
  };
  return sizes[size] || 'text-base';
};

// Helper para obtener clases de peso de fuente
const getFontWeightClass = (weight) => {
  const weights = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold'
  };
  return weights[weight] || 'font-normal';
};

// Helper para obtener clases de familia de fuente
const getFontFamilyClass = (family) => {
  const families = {
    sans: 'font-sans',
    serif: 'font-serif',
    mono: 'font-mono'
  };
  return families[family] || 'font-sans';
};

// Helper para obtener valor de redondeo
const getBorderRadius = (radius) => {
  const radii = {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px'
  };
  return radii[radius] || '0.5rem';
};

// Helper para obtener ancho del modal
const getModalMaxWidth = (width) => {
  const widths = {
    sm: '384px',
    md: '448px',
    lg: '512px',
    xl: '576px',
    '2xl': '672px'
  };
  return widths[width] || '512px';
};

// Helper para obtener sombra del modal
const getBoxShadow = (shadow) => {
  const shadows = {
    none: 'none',
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)'
  };
  return shadows[shadow] || shadows.lg;
};

// Helper para obtener clases de animación
const getAnimationClass = (animacion, duracion) => {
  if (animacion === 'none') return '';
  const duration = `${duracion}ms`;
  const animations = {
    fade: `animate-in fade-in duration-[${duration}]`,
    slide: `animate-in slide-in-from-bottom-4 duration-[${duration}]`,
    zoom: `animate-in zoom-in-95 duration-[${duration}]`,
    bounce: `animate-in zoom-in-95 duration-[${duration}]`
  };
  return animations[animacion] || '';
};

// Variables dinámicas disponibles para personalización
const VARIABLES_DISPONIBLES = {
  'nombre.usuario': (user) => user?.name || 'Usuario',
  'email.usuario': (user) => user?.email || '',
  'cargo.usuario': (user) => user?.cargo || '',
  'departamento.usuario': (user) => user?.departamento_nombre || '',
  'rol.usuario': (user) => user?.role || '',
  'fecha.actual': () => new Date().toLocaleDateString('es-ES'),
  'hora.actual': () => new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
  'dia.semana': () => new Date().toLocaleDateString('es-ES', { weekday: 'long' }),
};

// Función para reemplazar variables en el texto
const reemplazarVariables = (texto, user) => {
  if (!texto) return texto;
  
  let resultado = texto;
  for (const [variable, resolver] of Object.entries(VARIABLES_DISPONIBLES)) {
    const patron = new RegExp(`\\[${variable}\\]`, 'gi');
    resultado = resultado.replace(patron, resolver(user));
  }
  return resultado;
};

export default function PublicidadModal({ menuOverride, submenuOverride }) {
  const [publicidad, setPublicidad] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [open, setOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showZoom, setShowZoom] = useState(false); // Estado para ver imagen ampliada
  const location = useLocation();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    cargarConfiguracion();
    cargarPublicidad();
  }, [location.pathname]);

  // Deep-link desde el clic en la notificación de escritorio: ?pub_popup=ID
  // Fuerza el popup centrado con la imagen de esa publicidad.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const popupId = params.get('pub_popup');
    if (popupId) {
      cargarPublicidadPorId(popupId);
      // Limpiar el query param para que no se vuelva a disparar
      params.delete('pub_popup');
      const restante = params.toString();
      window.history.replaceState({}, '', location.pathname + (restante ? `?${restante}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const cargarConfiguracion = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/api/publicidades/configuracion-visual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log("Usando configuración por defecto");
    }
  };

  const obtenerMenuActual = () => {
    const path = location.pathname;
    
    // Buscar coincidencia exacta o por prefijo
    if (menuOverride) return { menu: menuOverride, submenu: submenuOverride };
    
    // Primero buscar coincidencia exacta
    if (RUTA_A_MENU[path]) {
      return { menu: RUTA_A_MENU[path], submenu: null };
    }
    
    // Luego buscar por prefijo
    for (const [ruta, menu] of Object.entries(RUTA_A_MENU)) {
      if (path.startsWith(ruta) && ruta !== '/') {
        return { menu, submenu: null };
      }
    }
    
    // Si no se encuentra, buscar el ID del submenú basado en la ruta
    const pathParts = path.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      // Convertir la ruta a un posible ID de submenú (ej: /gestion-humana/empleados -> empleados)
      return { menu: null, submenu: pathParts[pathParts.length - 1] };
    }
    
    return { menu: null, submenu: null };
  };

  const cargarPublicidad = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const { menu, submenu } = obtenerMenuActual();
      
      // Construir parámetros de query
      const params = new URLSearchParams();
      if (menu) params.append('menu_actual', menu);
      if (submenu) params.append('submenu_actual', submenu);
      
      const queryString = params.toString();
      const url = `${API_URL}/api/publicidades/activa${queryString ? '?' + queryString : ''}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setPublicidad(response.data);
        setOpen(true);
      }
    } catch (error) {
      console.log("No hay publicidad activa");
    }
  };

  const cargarPublicidadPorId = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/publicidades/popup/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setPublicidad(res.data);
        setOpen(true);
      }
    } catch (error) {
      console.log("No se pudo cargar la publicidad del popup");
    }
  };

  const handleClose = async () => {
    if (publicidad?.id) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `${API_URL}/api/publicidades/vista/${publicidad.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error("Error registrando vista:", error);
      }
    }
    setOpen(false);
  };

  const handleButtonClick = async () => {
    // Primero guardar la URL antes de cualquier operación async
    const urlDestino = publicidad?.boton_url;
    console.log("PublicidadModal - URL destino original:", urlDestino);
    
    if (publicidad?.id) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `${API_URL}/api/publicidades/${publicidad.id}/click`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error("Error registrando click:", error);
      }
    }
    
    // Abrir la URL en una nueva pestaña
    if (urlDestino) {
      // Asegurarse de que la URL tenga protocolo
      let urlFinal = urlDestino.trim();
      if (!urlFinal.startsWith('http://') && !urlFinal.startsWith('https://')) {
        urlFinal = 'https://' + urlFinal;
      }
      console.log("PublicidadModal - URL final a abrir:", urlFinal);
      
      // Abrir en nueva pestaña
      window.open(urlFinal, '_blank', 'noopener,noreferrer');
    }
    handleClose();
  };

  // Manejar click en múltiples botones
  const handleMultiButtonClick = async (url) => {
    console.log("PublicidadModal - Multi button URL:", url);
    
    if (publicidad?.id) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `${API_URL}/api/publicidades/${publicidad.id}/click`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error("Error registrando click:", error);
      }
    }
    
    // Abrir la URL en una nueva pestaña
    if (url) {
      let urlFinal = url.trim();
      if (!urlFinal.startsWith('http://') && !urlFinal.startsWith('https://')) {
        urlFinal = 'https://' + urlFinal;
      }
      console.log("PublicidadModal - URL final a abrir:", urlFinal);
      window.open(urlFinal, '_blank', 'noopener,noreferrer');
    }
    handleClose();
  };

  if (!publicidad) return null;

  // Estilos dinámicos basados en la configuración
  const modalStyles = {
    backgroundColor: config.color_fondo_modal,
    borderRadius: getBorderRadius(config.modal_redondeo),
    boxShadow: getBoxShadow(config.modal_sombra),
    maxWidth: getModalMaxWidth(config.modal_ancho),
    width: '100%',
    maxHeight: '92vh'
  };

  const imagenStyles = {
    maxHeight: `${config.imagen_altura}px`,
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: `${config.imagen_redondeo}px ${config.imagen_redondeo}px 0 0`
  };

  const contenidoStyles = {
    padding: `${config.padding_contenido}px`
  };

  const tituloStyles = {
    color: config.color_titulo,
    marginBottom: `${config.espacio_titulo_descripcion}px`
  };

  const descripcionStyles = {
    color: config.color_descripcion,
    marginBottom: `${config.espacio_descripcion_botones}px`
  };

  const botonPrincipalStyles = {
    backgroundColor: isHovering ? config.color_boton_hover : config.color_boton_fondo,
    color: config.color_boton_texto,
    borderRadius: getBorderRadius(config.boton_redondeo),
    padding: config.boton_tamaño === 'sm' ? '0.375rem 0.75rem' : config.boton_tamaño === 'lg' ? '0.75rem 1.5rem' : '0.5rem 1rem',
    transition: `background-color ${config.duracion_animacion}ms ease`
  };

  const botonCerrarStyles = {
    color: config.color_boton_cerrar_texto,
    backgroundColor: config.color_boton_cerrar
  };

  const degradadoStyles = config.mostrar_degradado_imagen ? {
    background: `linear-gradient(to bottom, ${config.color_degradado_inicio}, ${config.color_degradado_fin})`
  } : {};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent 
        className="p-0 overflow-hidden border-0 bg-transparent"
        style={{ maxWidth: getModalMaxWidth(config.modal_ancho) }}
        data-testid="publicidad-modal"
      >
        <VisuallyHidden>
          <DialogTitle>Publicidad</DialogTitle>
        </VisuallyHidden>
        
        {/* Overlay personalizado */}
        <div 
          className="fixed inset-0 -z-10"
          style={{ 
            backgroundColor: config.color_overlay,
            opacity: config.opacidad_overlay / 100
          }}
        />
        
        <div 
          className={`relative overflow-y-auto overflow-x-hidden ${getAnimationClass(config.animacion_entrada, config.duracion_animacion)}`}
          style={modalStyles}
        >
          {/* Botón cerrar */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            data-testid="publicidad-cerrar-btn"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Imagen - Clickeable para ampliar. Se autoajusta y nunca se recorta. */}
          {publicidad.imagen_url && (
            <div 
              className="relative w-full flex items-center justify-center bg-white cursor-pointer group"
              onClick={() => setShowZoom(true)}
              title="Click para ampliar"
            >
              <img
                src={publicidad.imagen_url.startsWith('http') ? publicidad.imagen_url : `${API_URL}${publicidad.imagen_url}`}
                alt={publicidad.titulo || "Promoción"}
                className="w-auto max-w-full object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                style={{
                  borderRadius: `${config.imagen_redondeo}px`,
                  maxHeight: `min(${config.imagen_altura || 480}px, 70vh)`
                }}
                data-testid="publicidad-imagen"
              />
              {/* Indicador de zoom */}
              <div className="absolute bottom-6 right-6 bg-black/50 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                  <path d="M11 8v6"></path>
                  <path d="M8 11h6"></path>
                </svg>
                Click para ampliar
              </div>
              {config.mostrar_degradado_imagen && (
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    ...degradadoStyles,
                    borderRadius: `${config.imagen_redondeo}px`
                  }}
                />
              )}
            </div>
          )}

          {/* Contenido */}
          <div style={contenidoStyles}>
            {publicidad.titulo && (
              <h2 
                className={`${getFontSizeClass(config.tamaño_titulo)} ${getFontWeightClass(config.peso_titulo)} ${getFontFamilyClass(config.fuente_titulo)}`}
                style={tituloStyles}
                data-testid="publicidad-titulo"
              >
                {reemplazarVariables(publicidad.titulo, currentUser)}
              </h2>
            )}
            
            {publicidad.descripcion && (
              <p 
                className={`${getFontSizeClass(config.tamaño_descripcion)} leading-relaxed`}
                style={descripcionStyles}
                data-testid="publicidad-descripcion"
              >
                {reemplazarVariables(publicidad.descripcion, currentUser)}
              </p>
            )}

            {/* Botones - soporta hasta 3 botones personalizados */}
            <div 
              className="flex flex-wrap items-center"
              style={{ gap: `${config.espacio_entre_botones}px` }}
            >
              {/* Renderizar múltiples botones si existen */}
              {publicidad.botones && publicidad.botones.length > 0 ? (
                publicidad.botones.map((boton, index) => (
                  boton.texto && (
                    <button
                      key={index}
                      onClick={() => handleMultiButtonClick(boton.url)}
                      onMouseEnter={() => setIsHovering(index)}
                      onMouseLeave={() => setIsHovering(null)}
                      className="inline-flex items-center justify-center font-medium"
                      style={{
                        ...botonPrincipalStyles,
                        backgroundColor: isHovering === index ? config.color_boton_hover : config.color_boton_fondo,
                      }}
                      data-testid={`publicidad-accion-btn-${index}`}
                    >
                      {reemplazarVariables(boton.texto, currentUser)}
                      {boton.url && <ExternalLink className="ml-2 h-4 w-4" />}
                    </button>
                  )
                ))
              ) : (
                /* Fallback al botón único legacy */
                publicidad.boton_texto && (
                  <button
                    onClick={handleButtonClick}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    className="inline-flex items-center justify-center font-medium"
                    style={botonPrincipalStyles}
                    data-testid="publicidad-accion-btn"
                  >
                    {reemplazarVariables(publicidad.boton_texto, currentUser)}
                    {publicidad.boton_url && <ExternalLink className="ml-2 h-4 w-4" />}
                  </button>
                )
              )}
              
              <button
                onClick={handleClose}
                className="inline-flex items-center justify-center font-medium px-4 py-2 transition-colors hover:opacity-80"
                style={botonCerrarStyles}
                data-testid="publicidad-cerrar-btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Modal de Zoom para ver imagen ampliada */}
      {showZoom && publicidad?.imagen_url && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setShowZoom(false)}
          data-testid="publicidad-zoom-overlay"
        >
          {/* Botón cerrar */}
          <button
            onClick={() => setShowZoom(false)}
            className="absolute top-4 right-4 z-[101] bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
            data-testid="publicidad-zoom-cerrar"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Instrucciones */}
          <div className="absolute top-4 left-4 text-white/70 text-sm flex items-center gap-2">
            <span>Click en cualquier lugar para cerrar</span>
          </div>

          {/* Imagen ampliada */}
          <img
            src={publicidad.imagen_url.startsWith('http') ? publicidad.imagen_url : `${API_URL}${publicidad.imagen_url}`}
            alt={publicidad.titulo || "Promoción ampliada"}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="publicidad-zoom-imagen"
          />

          {/* Título si existe */}
          {publicidad.titulo && (
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <h3 className="text-white text-lg font-semibold bg-black/50 px-4 py-2 rounded-lg inline-block">
                {reemplazarVariables(publicidad.titulo, currentUser)}
              </h3>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
